<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';
import OverflowTooltip from '@/components/OverflowTooltip.vue';
import PeekHeader from '@/components/PeekHeader.vue';
import { INSPECTOR_HOST } from '@/composables/inspectorHostContext';
import { useIsPhone } from '@/composables/useIsPhone';
import { useInspectorStore } from '@/stores/inspectorStore';

/**
 * The peek on a phone: a bottom sheet that covers most of the screen, with a
 * grab handle. A phone has no room for a side peek, so this is the only peek
 * there.
 *
 * Sheets stack the way peeks do, with the same trail in the header. Swiping
 * the sheet down by more than 80 pixels, tapping ×, tapping the dimmed strip
 * above it, or Escape closes the whole stack. A shorter swipe springs back.
 *
 * It stops above the audio player, using the height the player publishes.
 */
const SWIPE_CLOSE_DISTANCE = 80;

const inspectorStore = useInspectorStore();
const isPhone = useIsPhone();

// Views shown here drill down with push(), so Back returns to the sheet below.
provide(INSPECTOR_HOST, true);

const isShown = computed(() => isPhone.value && inspectorStore.isOpen && !!inspectorStore.component);

const sheetRef = ref<HTMLElement | null>(null);
const dragOffset = ref(0);
const isDragging = ref(false);
let dragStartY = 0;

function onDragStart(event: PointerEvent) {
    // A tap on a button in the header is a tap, not the start of a swipe.
    if (event.target instanceof Element && event.target.closest('button, a, [role="button"]')) return;
    isDragging.value = true;
    dragStartY = event.clientY;
    document.addEventListener('pointermove', onDragMove);
    document.addEventListener('pointerup', onDragEnd);
    document.addEventListener('pointercancel', onDragEnd);
}

function onDragMove(event: PointerEvent) {
    if (!isDragging.value) return;
    // The sheet follows the finger down, never up past where it rests.
    dragOffset.value = Math.max(0, event.clientY - dragStartY);
}

function onDragEnd() {
    if (!isDragging.value) return;
    isDragging.value = false;
    document.removeEventListener('pointermove', onDragMove);
    document.removeEventListener('pointerup', onDragEnd);
    document.removeEventListener('pointercancel', onDragEnd);
    if (dragOffset.value > SWIPE_CLOSE_DISTANCE) {
        inspectorStore.close();
    }
    dragOffset.value = 0;
}

function onKeydown(event: KeyboardEvent) {
    if (event.key !== 'Escape' || !isShown.value) return;
    // A menu or dialog open on top takes its own Escape.
    if (document.querySelector('[data-dismissable-layer]')) return;
    event.preventDefault();
    inspectorStore.close();
}

// Focus the sheet itself when it opens, so the keyboard and screen readers land in it.
watch(isShown, async (shown) => {
    if (!shown) return;
    await nextTick();
    sheetRef.value?.focus({ preventScroll: true });
});

onMounted(() => document.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKeydown);
    onDragEnd();
});
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200"
      leave-active-class="transition-opacity duration-150"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isShown"
        data-testid="inspector-sheet-backdrop"
        class="fixed inset-x-0 top-0 bottom-[var(--global-audio-player-height,0px)] z-50 bg-black/55"
        @click="inspectorStore.close()"
      />
    </Transition>
    <Transition
      enter-active-class="transition-transform duration-300 ease-out"
      leave-active-class="transition-transform duration-200 ease-in"
      enter-from-class="translate-y-full"
      leave-to-class="translate-y-full"
    >
      <div
        v-if="isShown"
        ref="sheetRef"
        data-testid="inspector-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="inspector-sheet-title"
        tabindex="-1"
        class="elevated-surface fixed inset-x-0 top-[8dvh] bottom-[var(--global-audio-player-height,0px)] z-50 flex flex-col overflow-hidden rounded-t-3xl outline-none"
        :class="isDragging ? '' : 'transition-transform duration-200'"
        :style="dragOffset > 0 ? { transform: `translateY(${dragOffset}px)` } : undefined"
      >
        <!-- The grab handle and the header are where a swipe down starts. -->
        <div class="shrink-0 touch-none" data-testid="inspector-sheet-grab-area" @pointerdown="onDragStart">
          <div class="mx-auto mt-2 mb-1 h-1 w-9 rounded-full bg-muted-foreground/40" data-testid="inspector-sheet-handle" aria-hidden="true" />
          <PeekHeader />
        </div>
        <div class="min-h-0 flex-1 overflow-auto px-4 pt-2 pb-8">
          <h3 id="inspector-sheet-title" class="sr-only">{{ inspectorStore.title }}</h3>
          <div class="mb-4">
            <component
              :is="inspectorStore.headerComponent"
              v-if="inspectorStore.headerComponent"
              v-bind="{ ...inspectorStore.headerProps, size: 'title' }"
            />
            <OverflowTooltip
              v-else
              as="h2"
              :deps="inspectorStore.title"
              trigger-class="text-xl leading-tight font-semibold"
            >
              {{ inspectorStore.title }}
            </OverflowTooltip>
          </div>
          <component :is="inspectorStore.component" v-bind="inspectorStore.componentProps" />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
